// Gun assembly: recipe → parts → fit into the item cell at −25° (muzzle to the upper right) with gunPad
// padding × the tier scale, then the shared passes. `gunLayout` also returns the muzzle socket in frame
// units (0–100 per cell), used by tests/art and exported in catalog.json (Gallery muzzle FX, Unity).
// The Gallery draws the same frame rotated by −65° at runtime so the muzzle points up-screen (UX §6.3).
import type { Ctx2D } from '../../ctx.ts';
import { itemScale } from '../../items/kit.ts';
import { drawParts, outlineUnits, type Part } from '../../passes.ts';
import { DEG, type Pt } from '../../shapes.ts';
import type { ArtEnv, ItemSpec } from '../../types.ts';
import { barrel } from './barrel.ts';
import { decal } from './decal.ts';
import { finish } from './finish.ts';
import { grip } from './grip.ts';
import { magazine } from './magazine.ts';
import { muzzle } from './muzzle.ts';
import { receiver } from './receiver.ts';
import { GUN_DECAL, GUN_RECIPES } from './recipes.ts';
import { stock } from './stock.ts';
import { top } from './top.ts';
import type { GunKit, GunRecipe } from './types.ts';

export interface GunLayout {
  parts: Part[];
  /** gu → cell units. */
  k: number;
  angleDeg: number;
  /** Local bbox centre (gu). */
  lcx: number;
  lcy: number;
  /** Muzzle socket polygon in frame units (0–100 = one cell). */
  socket: Pt[];
  rim: boolean;
  glowTok: string | null;
  kit: GunKit;
}

export function gunRecipe(tier: number): GunRecipe {
  const r = GUN_RECIPES[Math.min(GUN_RECIPES.length, Math.max(1, tier)) - 1];
  if (!r) throw new Error('art: no gun recipes');
  return r;
}

export function gunLayout(spec: ItemSpec, px: number, env: ArtEnv): GunLayout {
  const { pal, sprites } = env;
  const r = gunRecipe(spec.tier);
  const g: GunKit = { pal, r, ow: 1 };
  const fin = finish(g);
  const dec = decal(g, GUN_DECAL[r.tier - 1] ?? 'ui.cream');
  const mz = muzzle(g);
  const parts: Part[] = [
    ...stock(g),
    grip(g),
    receiver(g, (c) => {
      dec(c);
      fin.stripe?.(c);
    }),
    ...barrel(g),
    ...magazine(g),
    mz.part,
    ...top(g),
  ];
  // Fit: the rotated extents of every part box's corners (tighter than the rotated union box).
  const a = pal.art.gunAngleDeg;
  const cos = Math.cos(a * DEG);
  const sin = Math.sin(a * DEG);
  let u0 = Number.POSITIVE_INFINITY;
  let v0 = Number.POSITIVE_INFINITY;
  let u1 = Number.NEGATIVE_INFINITY;
  let v1 = Number.NEGATIVE_INFINITY;
  for (const p of parts) {
    for (const [x, y] of [
      [p.box.x, p.box.y],
      [p.box.x + p.box.w, p.box.y],
      [p.box.x, p.box.y + p.box.h],
      [p.box.x + p.box.w, p.box.y + p.box.h],
    ] as const) {
      const u = x * cos - y * sin;
      const v = x * sin + y * cos;
      u0 = Math.min(u0, u);
      u1 = Math.max(u1, u);
      v0 = Math.min(v0, v);
      v1 = Math.max(v1, v);
    }
  }
  const avail =
    (1 - 2 * pal.art.gunPad) * 100 * itemScale(spec.tier, spec.tiers, sprites.itemScaleMin);
  const k = Math.min(avail / (u1 - u0), avail / (v1 - v0));
  // local point that lands on the cell centre: the rotated extents' centre, rotated back
  const uc = (u0 + u1) / 2;
  const vc = (v0 + v1) / 2;
  const lcx = uc * cos + vc * sin;
  const lcy = -uc * sin + vc * cos;
  const toFrame = ([x, y]: Pt): Pt => {
    const lx = (x - lcx) * k;
    const ly = (y - lcy) * k;
    return [50 + lx * cos - ly * sin, 50 + lx * sin + ly * cos];
  };
  g.ow = outlineUnits(sprites.outline, sprites.outlineMinPx, px, env.dpr) / k;
  return {
    parts,
    k,
    angleDeg: a,
    lcx,
    lcy,
    socket: mz.socket.map(toFrame),
    rim: fin.rim,
    glowTok: fin.glowTok,
    kit: g,
  };
}

export function drawGun(c: Ctx2D, spec: ItemSpec, px: number, env: ArtEnv): void {
  const { pal, sprites } = env;
  const L = gunLayout(spec, px, env);
  c.save();
  c.scale(px / 100, px / 100);
  c.translate(50, 50);
  c.rotate(L.angleDeg * DEG);
  c.scale(L.k, L.k);
  c.translate(-L.lcx, -L.lcy);
  drawParts(c, L.parts, {
    ow: L.kit.ow,
    rim: L.rim,
    halo: { w: (sprites.halo * 100) / L.k, color: pal.hex('ui.cream'), alpha: sprites.haloAlpha },
    glow: L.glowTok
      ? { color: pal.rgba(L.glowTok, pal.art.glowAlpha, 'glow'), blur: pal.art.glowBlur * px }
      : null,
    shadeBand: pal.rules.shadeBand,
    hiW: 2.6,
  });
  c.restore();
}
